/**
 * Platform Admin · Organizations (list + detail pages)
 * Driven by ``platform/organizations/list.html`` and ``detail.html``.
 *
 * Two responsibilities:
 *
 *   1. Filter form (search + plan + status + Reset). Pure HTMX — the
 *      ``<select>``s have their own ``hx-get`` and the search input
 *      submits the parent form on Enter. No JS needed for filtering.
 *   2. Action buttons (extend trial / make unlimited / revert /
 *      cancel deletion / deactivate / reactivate / delete). Each button
 *      carries ``data-org-action``, ``data-org-url`` and
 *      ``data-org-name``. A single delegated click handler on
 *      ``document`` opens the global confirm modal with the right
 *      title/message/type, then POSTs via ``htmx.ajax`` so the page
 *      section re-renders without a full reload.
 *
 *      Optional per-button attributes:
 *        - ``data-org-target``  htmx swap target/select selector
 *                               (default ``#list-results`` — the list).
 *                               The detail page uses ``#org-detail``.
 *        - ``data-org-next``    extra ``next`` POST value so the view
 *                               redirects back to the same page
 *                               (``detail``).
 *        - ``data-org-native``  "1" → confirm submits a real HTML form
 *                               instead of ``htmx.ajax``. Needed when
 *                               the action's redirect lands on a
 *                               DIFFERENT page (e.g. permanent delete
 *                               from the detail page → back to the
 *                               list): an XHR would follow the redirect
 *                               and then fail to select the swap target
 *                               in the foreign page's HTML.
 *
 * The translated labels live in a ``{{ ...|json_script:"org-action-labels" }}``
 * block in the template — read dynamically on every click so that, after
 * a boosted nav or history restore, we always pick up the labels from
 * the *current* page render (and the *current* language).
 *
 * See ``.cursor/rules/hx-boost-init.mdc`` rule 1: the document-level
 * click delegation is bound exactly once for the SPA lifetime via
 * ``bindGlobalsOnce``. Without that guard, every boosted nav landing on
 * this page would stack another listener on ``document`` → each click
 * on an action button would open *N* confirm modals and (worse) fire
 * *N* POSTs on confirm. ``autoInit`` itself stays cheap — it just calls
 * ``bindGlobalsOnce`` and re-binds the create-org modal helper.
 *
 * Create-org modal: the "Create" button on the list carries
 * ``data-org-create-open`` (+ url/title data-attrs) and opens the
 * static ``#org-create-modal`` shell through the canonical
 * dynamic-modal helper (``form-modals.mdc``). The shell sits OUTSIDE
 * ``#list-results`` so filter/pagination swaps never orphan it, but it
 * IS replaced on boosted navs — hence the afterSettle re-bind below.
 */

import { createDynamicModal } from '../utils/ui/dynamic-modal.js';

let _globalBound = false;
let orgCreateModalHelper = null;

function getActionLabels() {
  const el = document.getElementById('org-action-labels');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

function getCsrfToken() {
  const el = document.querySelector('[name=csrfmiddlewaretoken]');
  if (el && el.value) return el.value;
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? m[1] : '';
}

function submitNativeForm(url, values) {
  const form = document.createElement('form');
  form.method = 'post';
  form.action = url;
  values.csrfmiddlewaretoken = getCsrfToken();
  Object.keys(values).forEach(function (name) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = values[name];
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

function handleActionClick(evt) {
  const btn = evt.target.closest('[data-org-action]');
  if (!btn) return;

  const action = btn.dataset.orgAction;
  const url = btn.dataset.orgUrl;
  const orgName = btn.dataset.orgName || '';
  const target = btn.dataset.orgTarget || '#list-results';
  const labels = getActionLabels();
  const spec = labels && labels[action];
  if (!spec || !url) return;

  const values = { action: action };
  if (btn.dataset.orgNext) values.next = btn.dataset.orgNext;

  if (typeof window.showConfirmModal !== 'function') return;
  window.showConfirmModal({
    title: spec.title,
    message: orgName + ' — ' + spec.message,
    confirmText: spec.confirm,
    type: spec.type,
    onConfirm: function () {
      if (btn.dataset.orgNative === '1') {
        submitNativeForm(url, values);
        return;
      }
      // CSRF is added automatically by the global ``htmx:configRequest``
      // hook in app.js (X-CSRFToken header). No need to ship a token in
      // ``values``, which would also bake the initial render's token
      // into a cached HTML snapshot served back via Back/Forward.
      window.htmx.ajax('POST', url, {
        target: target,
        select: target,
        swap: 'outerHTML',
        values: values,
      });
    },
  });
}

function initOrgCreateModal() {
  if (orgCreateModalHelper) return;
  // Returns null when the shell is absent (e.g. the detail page reuses
  // this auto-init but has no create modal).
  orgCreateModalHelper = createDynamicModal({
    modalId: 'org-create-modal',
    onSuccess: function (data, helper) {
      if (data && data.message && typeof window.showToast === 'function') {
        window.showToast(data.message, 'success');
      }
      helper.close(function () {
        // Refresh only the list section — same swap as the filter form.
        if (window.htmx) {
          window.htmx.ajax('GET', window.location.href, {
            target: '#list-results',
            select: '#list-results',
            swap: 'outerHTML',
          });
        }
      });
    },
    onContentInjected: function () {
      // Custom-select wrapper for the language <select> — must run on
      // every inject (initial GET + 422 re-render). form-modals.mdc §5.
      if (typeof window.initCustomSelects === 'function') {
        window.initCustomSelects();
      }
    },
  });
}

function handleCreateClick(evt) {
  const btn = evt.target.closest('[data-org-create-open]');
  if (!btn || !orgCreateModalHelper) return;
  evt.preventDefault();
  orgCreateModalHelper.open(
    btn.dataset.orgCreateUrl,
    btn.dataset.orgCreateTitle || 'Create'
  );
}

function handleAfterSettle(evt) {
  const target = evt.detail && evt.detail.target;
  if (!target || target.id !== 'platform-content') return;
  // The modal shell was replaced by a boosted nav — the cached helper
  // now points at an orphan node. Re-bind to the fresh shell (no-op if
  // the new page has none). See ``form-modals.mdc`` §9.
  orgCreateModalHelper = null;
  initOrgCreateModal();
}

function bindGlobalsOnce() {
  if (_globalBound) return;
  _globalBound = true;
  document.addEventListener('click', handleActionClick);
  document.addEventListener('click', handleCreateClick);
  document.body.addEventListener('htmx:afterSettle', handleAfterSettle);
}

export function initOrganizationsList() {
  bindGlobalsOnce();
  orgCreateModalHelper = null;
  initOrgCreateModal();
}

/**
 * Auto-init entry point (wired via ``data-auto-init="organizationsList"``).
 * Called by ``_runAutoInit`` in app.js on initial DOMContentLoaded, on
 * htmx:afterSwap of #platform-content (boosted navigation landing on
 * this page), and on htmx:historyRestore (browser Back/Forward).
 */
export function autoInitOrganizationsList() {
  const root = document.querySelector('[data-auto-init="organizationsList"]');
  if (!root) return;
  initOrganizationsList();
}
